import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Loader2, MoreVertical, Plus } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/api";
import StatusBadge from "@/components/StatusBadge";
import ProtectedImage from "@/components/ProtectedImage";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import ResponsiveFormDialog from "@/components/ResponsiveFormDialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { EmptyState, ErrorState, ListSkeleton, Refreshing } from "@/components/admin/AsyncView";
import ConfirmDialog from "@/components/admin/ConfirmDialog";
import ItemForm, { validateItem } from "@/components/admin/ItemForm";

const EMPTY_ITEM = { name: "", category: "", item_code: "", photo: "", condition: "BAIK", location: "Storage Utama", quantity: 1 };
const SORTS = {
  name: { label: "Nama", fn: (a, b) => a.name.localeCompare(b.name) },
  qty: { label: "Stok terbanyak", fn: (a, b) => (b.quantity || 0) - (a.quantity || 0) },
  cat: { label: "Kategori", fn: (a, b) => (a.category || "").localeCompare(b.category || "") || a.name.localeCompare(b.name) },
};

export default function InventoryPage() {
  const [params, setParams] = useSearchParams();
  const q = params.get("q") || "";
  const cat = params.get("cat") || "";
  const status = params.get("status") || "";
  const sort = SORTS[params.get("sort")] ? params.get("sort") : "name";
  const view = params.get("view") === "kategori" ? "kategori" : "items";

  const [items, setItems] = useState(null);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);

  const [stockDrafts, setStockDrafts] = useState({});
  const [savingStock, setSavingStock] = useState(null);
  const [dialog, setDialog] = useState(null); // {mode:'add'} | {mode:'edit', item}
  const [form, setForm] = useState(EMPTY_ITEM);
  const [photoFile, setPhotoFile] = useState(null);
  const [uploadedPath, setUploadedPath] = useState(null);
  const [uploadPct, setUploadPct] = useState(null);
  const [formErrors, setFormErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [confirmMaint, setConfirmMaint] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [newCategory, setNewCategory] = useState("");
  const [renaming, setRenaming] = useState(null);
  const summaryRef = useRef(null);

  const load = useCallback(async (background = false) => {
    if (background) setRefreshing(true);
    else { setLoading(true); setError(null); }
    try {
      const [itemsRes, catRes] = await Promise.all([api.get("/items", { params: { include_unbookable: true } }), api.get("/categories")]);
      setItems(itemsRes.data);
      setCategories(catRes.data);
      setError(null);
    } catch (e) {
      if (!background) setError(errMsg(e));
      else toast.error(`Tidak dapat memperbarui: ${errMsg(e)}. Menampilkan data terakhir.`);
    } finally {
      setLoading(false); setRefreshing(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const set = (key, value, replace = false) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value); else next.delete(key);
    setParams(next, { replace });
  };

  const toggleView = () => set("view", view === "items" ? "kategori" : null, true);

  const filtered = useMemo(() => {
    const list = items || [];
    const needle = q.trim().toLowerCase();
    return list
      .filter((i) => (!needle || i.name?.toLowerCase().includes(needle) || i.item_code?.toLowerCase().includes(needle))
        && (!cat || i.category === cat) && (!status || i.status === status))
      .sort(SORTS[sort].fn);
  }, [items, q, cat, status, sort]);

  // --- form tambah/edit ---
  const openAdd = () => {
    setDialog({ mode: "add" });
    setForm(EMPTY_ITEM);
    setPhotoFile(null); setUploadedPath(null); setUploadPct(null);
    setFormErrors({}); setDirty(false);
  };
  const openEdit = (item) => {
    setDialog({ mode: "edit", item });
    setForm({ ...item });
    setPhotoFile(null); setUploadedPath(null); setUploadPct(null);
    setFormErrors({}); setDirty(false);
  };
  const changeForm = (v) => { setForm(v); setDirty(true); };
  const changePhoto = (f) => { setPhotoFile(f); setUploadedPath(null); setUploadPct(null); setDirty(true); };
  const tryCloseDialog = () => {
    if (dirty && !saving) setConfirmDiscard(true);
    else setDialog(null);
  };
  const blurField = (name) => {
    const errs = validateItem(form, { hasPhoto: !!(photoFile || uploadedPath || form.photo) });
    setFormErrors((prev) => {
      const next = { ...prev };
      if (errs[name]) next[name] = errs[name];
      else delete next[name];
      return next;
    });
  };

  const uploadPhoto = async (file) => {
    const data = new FormData();
    data.append("file", file);
    setUploadPct(0);
    const { data: res } = await api.post("/admin/items/photo", data, {
      headers: { "Content-Type": "multipart/form-data" },
      onUploadProgress: (e) => {
        if (e.total) setUploadPct(Math.round((e.loaded / e.total) * 100));
      },
    });
    setUploadPct(null);
    return res.path;
  };

  const saveForm = async () => {
    const errs = validateItem(form, { hasPhoto: !!(photoFile || uploadedPath || form.photo) });
    setFormErrors(errs);
    if (Object.keys(errs).length > 0) {
      summaryRef.current?.focus();
      return;
    }
    setSaving(true);
    try {
      // Unggah sekali; bila simpan data gagal, path terunggah dipakai ulang.
      let photoPath = uploadedPath;
      if (photoFile && !photoPath) {
        photoPath = await uploadPhoto(photoFile);
        setUploadedPath(photoPath);
      }
      const payload = { ...form, quantity: Number(form.quantity), photo: photoPath || form.photo };
      if (dialog.mode === "add") {
        await api.post("/items", payload);
        toast.success("Alat ditambahkan");
      } else {
        await api.put(`/items/${dialog.item.id}`, payload);
        toast.success("Alat diperbarui");
      }
      setDialog(null);
      setDirty(false);
      await load(true);
    } catch (e) {
      // Input dan file dipertahankan - tidak ada yang di-reset di sini.
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
      setUploadPct(null);
    }
  };

  // --- stok eksplisit per baris ---
  const draftFor = (item) => stockDrafts[item.id] ?? item.quantity;
  const setDraft = (item, v) => setStockDrafts((d) => ({ ...d, [item.id]: v }));
  const saveStock = async (item) => {
    const qty = Number(draftFor(item));
    if (!Number.isInteger(qty) || qty < 1) {
      toast.error("Jumlah stok minimal 1.");
      return;
    }
    setSavingStock(item.id);
    try {
      await api.put(`/items/${item.id}`, { ...item, quantity: qty });
      toast.success("Stok disimpan");
      setStockDrafts((d) => { const n = { ...d }; delete n[item.id]; return n; });
      await load(true);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSavingStock(null);
    }
  };

  const doMaintenance = async (item) => {
    const to = item.status === "MAINTENANCE" ? "AVAILABLE" : "MAINTENANCE";
    setBusyId(item.id);
    try {
      await api.put(`/items/${item.id}`, { ...item, status: to });
      toast.success(to === "MAINTENANCE" ? "Alat masuk maintenance" : "Alat aktif kembali");
      await load(true);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusyId(null);
      setConfirmMaint(null);
    }
  };

  const doDelete = async (item) => {
    setBusyId(item.id);
    try {
      await api.delete(`/items/${item.id}`);
      toast.success("Alat dihapus");
      await load(true);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusyId(null);
      setConfirmDelete(null);
    }
  };

  const saveCategory = async (fn, msg) => {
    setBusyId("cat");
    try { await fn(); toast.success(msg); await load(true); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setBusyId(null); }
  };

  if (loading) {
    return (
      <div className="space-y-5">
        <InventoryHeader onAdd={openAdd} onToggleView={toggleView} view={view} />
        <ListSkeleton rows={6} />
      </div>
    );
  }
  if (error && !items) {
    return (
      <div className="space-y-5">
        <InventoryHeader onAdd={openAdd} onToggleView={toggleView} view={view} />
        <ErrorState message={error} onRetry={() => load()} />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <InventoryHeader refreshing={refreshing} onRefresh={() => load(true)} onAdd={openAdd} view={view} onToggleView={toggleView} />

      {view === "kategori" ? (
        <CategorySection
          categories={categories}
          newCategory={newCategory}
          setNewCategory={setNewCategory}
          renaming={renaming}
          setRenaming={setRenaming}
          busy={busyId === "cat"}
          onAdd={() => newCategory.trim() && saveCategory(() => api.post("/categories", { name: newCategory.trim() }).then(() => setNewCategory("")), "Kategori ditambahkan")}
          onRename={(oldName, value) => saveCategory(() => api.put(`/categories/${encodeURIComponent(oldName)}`, { new_name: value }).then(() => setRenaming(null)), "Kategori diubah")}
          onDelete={(name) => saveCategory(() => api.delete(`/categories/${encodeURIComponent(name)}`), "Kategori dihapus")}
        />
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-2">
            <label className="flex h-10 min-w-0 basis-full items-center gap-2 rounded-lg border border-white/10 bg-zinc-900 px-3 sm:basis-auto sm:flex-1 sm:max-w-xs">
              <span className="sr-only">Cari nama atau kode alat</span>
              <Input data-testid="inventory-search" value={q} onChange={(e) => set("q", e.target.value || null, true)}
                placeholder="Cari nama atau kode" className="h-full border-0 bg-transparent p-0 text-sm placeholder:text-zinc-600 focus-visible:ring-0" />
            </label>
            <select data-testid="inventory-category-filter" value={cat} onChange={(e) => set("cat", e.target.value || null, true)}
              aria-label="Filter kategori" className="h-10 rounded-lg border border-white/10 bg-zinc-900 px-2 text-sm">
              <option value="">Semua kategori</option>
              {categories.map((c) => <option key={c.name} value={c.name}>{c.name}</option>)}
            </select>
            <select data-testid="inventory-status-filter" value={status} onChange={(e) => set("status", e.target.value || null, true)}
              aria-label="Filter status" className="h-10 rounded-lg border border-white/10 bg-zinc-900 px-2 text-sm">
              <option value="">Semua status</option>
              <option value="AVAILABLE">Tersedia</option>
              <option value="MAINTENANCE">Maintenance</option>
            </select>
            <select data-testid="inventory-sort" value={sort} onChange={(e) => set("sort", e.target.value, true)}
              aria-label="Urutkan" className="h-10 rounded-lg border border-white/10 bg-zinc-900 px-2 text-sm">
              {Object.entries(SORTS).map(([k, s]) => <option key={k} value={k}>Urut: {s.label.toLowerCase()}</option>)}
            </select>
            <span data-testid="inventory-count" className="ml-auto text-xs tabular-nums text-zinc-500">
              {filtered.length} dari {items.length} jenis
            </span>
          </div>

          {filtered.length === 0 ? (
            <EmptyState
              kind={q || cat || status ? "filtered" : "first"}
              title={q || cat || status ? "Tidak ada hasil" : "Belum ada alat"}
              hint={q || cat || status ? "Coba ubah kata kunci atau bersihkan filter." : "Tambahkan alat pertama lewat tombol Tambah alat."}
              action={(q || cat || status) && (
                <Button variant="outline" onClick={() => setParams({}, { replace: true })}
                  className="h-10 rounded-full border-white/15 bg-transparent px-4 text-sm text-zinc-200 hover:bg-white/10">
                  Bersihkan filter
                </Button>
              )}
            />
          ) : (
            <>
              <div className="space-y-2 lg:hidden">
                {filtered.map((item) => (
                  <MobileItemCard key={item.id} item={item}
                    draft={draftFor(item)} dirtyStock={Number(draftFor(item)) !== Number(item.quantity)}
                    savingRow={savingStock === item.id}
                    onDraft={(v) => setDraft(item, v)} onSaveStock={() => saveStock(item)}
                    onEdit={() => openEdit(item)}
                    onMaintenance={() => setConfirmMaint(item)} onDelete={() => setConfirmDelete(item)}
                    busy={busyId === item.id} />
                ))}
              </div>
              <div className="hidden overflow-x-auto rounded-2xl border border-white/10 bg-zinc-900 lg:block">
                <table className="w-full min-w-[760px] border-collapse text-sm" aria-label="Daftar alat">
                  <thead>
                    <tr className="border-b border-white/10 text-left text-xs font-medium text-zinc-500">
                      <th scope="col" className="px-4 py-2.5 font-medium">Alat</th>
                      <th scope="col" className="px-4 py-2.5 font-medium">Kategori</th>
                      <th scope="col" className="px-4 py-2.5 font-medium">Lokasi</th>
                      <th scope="col" className="px-4 py-2.5 text-right font-medium">Stok</th>
                      <th scope="col" className="px-4 py-2.5 font-medium">Status</th>
                      <th scope="col" className="px-4 py-2.5 text-right font-medium">Aksi</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((item) => {
                      const draft = draftFor(item);
                      const dirtyStock = Number(draft) !== Number(item.quantity);
                      const savingRow = savingStock === item.id;
                      return (
                        <tr key={item.id} data-testid={`inventory-row-${item.item_code}`} className="border-b border-white/5 last:border-0 hover:bg-white/[0.02]">
                          <td className="px-4 py-3">
                            <div className="flex min-w-0 items-center gap-3">
                              <ProtectedImage src={item.photo} alt="" className="h-12 w-12 shrink-0 rounded-lg border border-white/10 object-cover" />
                              <div className="min-w-0">
                                <p className="break-words font-medium text-zinc-100">{item.name}</p>
                                <p className="font-mono text-xs text-zinc-500">{item.item_code}</p>
                              </div>
                            </div>
                          </td>
                          <td className="whitespace-nowrap px-4 py-3 text-zinc-300">{item.category || "-"}</td>
                          <td className="whitespace-nowrap px-4 py-3 text-zinc-400">{item.location || "-"}</td>
                          <td className="px-4 py-3">
                            <div className="flex items-center justify-end gap-1.5">
                              <Input
                                data-testid={`stock-input-${item.item_code}`}
                                type="number" min="1" step="1"
                                value={draft}
                                disabled={savingRow}
                                onChange={(e) => setDraft(item, e.target.value === "" ? "" : Number(e.target.value))}
                                aria-label={`Stok ${item.name}`}
                                className="h-9 w-[72px] rounded-lg border-white/10 bg-zinc-950 text-right text-sm tabular-nums"
                              />
                              <Button
                                data-testid={`stock-save-${item.item_code}`}
                                size="sm"
                                disabled={!dirtyStock || savingRow}
                                title={dirtyStock ? "Simpan perubahan stok" : "Stok belum berubah"}
                                onClick={() => saveStock(item)}
                                className="h-9 rounded-lg bg-[#0069DB] px-3 text-xs font-semibold text-white hover:bg-[#0058B6] disabled:bg-white/5 disabled:text-zinc-500"
                              >
                                {savingRow ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Simpan"}
                              </Button>
                            </div>
                          </td>
                          <td className="whitespace-nowrap px-4 py-3"><StatusBadge status={item.status} /></td>
                          <td className="px-4 py-3">
                            <div className="flex items-center justify-end gap-1">
                              <button
                                data-testid={`edit-item-${item.item_code}`}
                                onClick={() => openEdit(item)}
                                className="min-h-[44px] rounded-lg px-3 text-xs font-medium text-zinc-300 hover:bg-white/10 hover:text-white"
                              >
                                Edit
                              </button>
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <button aria-label={`Aksi lain untuk ${item.name}`} className="rounded-lg p-2 text-zinc-400 hover:bg-white/10 hover:text-white min-h-[44px] min-w-[44px]">
                                    <MoreVertical className="h-4 w-4" />
                                  </button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="border-white/10 bg-zinc-900 text-zinc-100">
                                  <DropdownMenuItem
                                    data-testid={`toggle-maintenance-${item.item_code}`}
                                    disabled={busyId === item.id}
                                    onClick={() => setConfirmMaint(item)}
                                  >
                                    {item.status === "MAINTENANCE" ? "Aktifkan kembali" : "Masuk maintenance"}
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    data-testid={`delete-item-${item.item_code}`}
                                    disabled={busyId === item.id}
                                    onClick={() => setConfirmDelete(item)}
                                    className="text-rose-300 focus:text-rose-200"
                                  >
                                    Hapus
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <p className="text-xs text-zinc-500">
                Stok adalah total unit milik storage. Ketersediaan pada tanggal tertentu dihitung saat booking, bukan angka ini.
              </p>
            </>
          )}
        </>
      )}

      <ResponsiveFormDialog
        open={!!dialog}
        onOpenChange={(o) => { if (!o) tryCloseDialog(); }}
        title={dialog?.mode === "add" ? "Tambah alat" : "Edit alat"}
        description="Lengkapi data alat. Tanda bintang wajib diisi."
      >
        {dialog && (
          <>
            {Object.keys(formErrors).length > 0 && (
              <div ref={summaryRef} tabIndex={-1} role="alert" data-testid="item-form-errors"
                className="mb-4 rounded-xl border border-rose-500/40 bg-rose-500/10 px-4 py-3 text-sm outline-none focus:border-rose-400">
                <p className="font-semibold text-rose-200">Belum bisa disimpan.</p>
                <ul className="mt-1 list-disc space-y-0.5 pl-5 text-rose-200/90">
                  {Object.values(formErrors).map((m, i) => <li key={i}>{m}</li>)}
                </ul>
              </div>
            )}
            <ItemForm
              value={form}
              onChange={changeForm}
              categories={categories}
              prefix={dialog.mode === "add" ? "item" : "edit-item"}
              photoFile={photoFile}
              onPhotoFileChange={changePhoto}
              uploadedPath={uploadedPath}
              uploadPct={uploadPct}
              errors={formErrors}
              onBlurField={blurField}
            />
            <div className="sticky bottom-0 -mx-1 mt-4 flex justify-end gap-2 bg-zinc-900 px-1 py-3">
              <Button variant="outline" disabled={saving} onClick={tryCloseDialog}
                className="h-12 rounded-full border-white/15 bg-transparent px-5 text-zinc-200 hover:bg-white/10">
                Batal
              </Button>
              <Button data-testid={dialog.mode === "add" ? "save-item-button" : "save-item-edit-button"}
                disabled={saving} onClick={saveForm}
                className="h-12 rounded-full bg-[#0069DB] px-6 font-semibold text-white hover:bg-[#0058B6]">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : dialog.mode === "add" ? "Simpan alat" : "Simpan perubahan"}
              </Button>
            </div>
          </>
        )}
      </ResponsiveFormDialog>

      <ConfirmDialog
        open={confirmDiscard}
        onOpenChange={setConfirmDiscard}
        title="Buang perubahan?"
        description="Formulir sudah diubah. Menutup sekarang akan membuang isian dan foto yang dipilih."
        confirmLabel="Buang"
        testId="discard-confirm"
        onConfirm={() => { setConfirmDiscard(false); setDialog(null); setDirty(false); }}
      />

      <ConfirmDialog
        open={!!confirmMaint}
        onOpenChange={(o) => !o && setConfirmMaint(null)}
        title={confirmMaint?.status === "MAINTENANCE" ? `Aktifkan ${confirmMaint?.name}?` : `Maintenance ${confirmMaint?.name}?`}
        description={confirmMaint?.status === "MAINTENANCE"
          ? "Alat kembali bisa dipinjam."
          : "Alat tidak bisa dipinjam selama maintenance. Booking berjalan tidak terganggu."}
        confirmLabel={confirmMaint?.status === "MAINTENANCE" ? "Aktifkan" : "Masuk maintenance"}
        danger={false}
        testId="maintenance-confirm"
        pending={confirmMaint && busyId === confirmMaint.id}
        onConfirm={() => confirmMaint && doMaintenance(confirmMaint)}
      />

      <ConfirmDialog
        open={!!confirmDelete}
        onOpenChange={(o) => !o && setConfirmDelete(null)}
        title={`Hapus ${confirmDelete?.name}?`}
        description={`Kode ${confirmDelete?.item_code} dihapus permanen dari katalog. Tidak bisa dihapus bila masih dipakai booking aktif atau menghambat.`}
        confirmLabel="Hapus permanen"
        testId="delete-item-confirm"
        pending={confirmDelete && busyId === confirmDelete.id}
        onConfirm={() => confirmDelete && doDelete(confirmDelete)}
      />
    </div>
  );
}

// Kartu dua baris di bawah 768px: identitas + angka kunci di baris 1,
// sekunder di baris 2, aksi (termasuk edit stok eksplisit) di baris 3.
function MobileItemCard({ item, draft, dirtyStock, savingRow, onDraft, onSaveStock, onEdit, onMaintenance, onDelete, busy }) {
  return (
    <article data-testid={`m-inventory-card-${item.item_code}`} className="rounded-2xl border border-white/10 bg-zinc-900 p-4">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="break-words text-sm font-semibold text-zinc-100">{item.name}</p>
          <p className="mt-0.5 font-mono text-xs tabular-nums text-zinc-500">{item.item_code}</p>
        </div>
        <StatusBadge status={item.status} />
      </div>
      <p className="mt-1.5 text-xs text-zinc-500">
        {[item.category, item.location].filter(Boolean).join(" - ") || "-"}
        <span className="tabular-nums"> - stok {item.quantity} pcs</span>
      </p>
      <div className="mt-3 flex flex-wrap items-center gap-1.5">
        <Input
          data-testid={`m-stock-input-${item.item_code}`}
          type="number" min="1" step="1"
          value={draft}
          disabled={savingRow}
          onChange={(e) => onDraft(e.target.value === "" ? "" : Number(e.target.value))}
          aria-label={`Stok ${item.name}`}
          className="h-11 w-20 rounded-lg border-white/10 bg-zinc-950 text-sm tabular-nums"
        />
        <Button
          data-testid={`m-stock-save-${item.item_code}`}
          disabled={!dirtyStock || savingRow}
          onClick={onSaveStock}
          className="h-11 rounded-full bg-[#0069DB] px-4 text-xs font-semibold text-white hover:bg-[#0058B6] disabled:bg-white/5 disabled:text-zinc-500"
        >
          {savingRow ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Simpan"}
        </Button>
        <span className="flex-1" />
        <button
          data-testid={`m-edit-item-${item.item_code}`}
          onClick={onEdit}
          className="min-h-[44px] rounded-lg px-3 text-xs font-medium text-zinc-300 hover:bg-white/10 hover:text-white"
        >
          Edit
        </button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button aria-label={`Aksi lain untuk ${item.name}`} className="min-h-[44px] min-w-[44px] rounded-lg p-2 text-zinc-400 hover:bg-white/10 hover:text-white">
              <MoreVertical className="h-4 w-4" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="border-white/10 bg-zinc-900 text-zinc-100">
            <DropdownMenuItem disabled={busy} onClick={onMaintenance}>
              {item.status === "MAINTENANCE" ? "Aktifkan kembali" : "Masuk maintenance"}
            </DropdownMenuItem>
            <DropdownMenuItem disabled={busy} onClick={onDelete} className="text-rose-300 focus:text-rose-200">
              Hapus
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </article>
  );
}

function InventoryHeader({ refreshing, onRefresh, onAdd, view, onToggleView }) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <h1 className="text-2xl font-semibold tracking-tight text-white">Inventaris</h1>
      {refreshing ? <Refreshing /> : onRefresh && (
        <button onClick={onRefresh} className="text-xs text-zinc-500 underline-offset-4 hover:text-zinc-300 hover:underline">Muat ulang</button>
      )}
      <span className="flex-1" />
      {onToggleView && (
      <Button data-testid="toggle-category-view" variant="outline" onClick={onToggleView}
        className="h-10 rounded-full border-white/15 bg-transparent px-4 text-sm text-zinc-200 hover:bg-white/10">
        {view === "kategori" ? "Kembali ke daftar" : "Kelola kategori"}
      </Button>
      )}
      <Button data-testid="add-item-button" onClick={onAdd}
        className="h-10 rounded-full bg-[#0069DB] px-4 text-sm font-semibold text-white hover:bg-[#0058B6]">
        <Plus className="h-4 w-4" /> Tambah alat
      </Button>
      <p className="w-full text-sm text-zinc-400">Stok di sini total unit milik storage, bukan janji ketersediaan tanggal tertentu.</p>
    </div>
  );
}

function CategorySection({ categories, newCategory, setNewCategory, renaming, setRenaming, busy, onAdd, onRename, onDelete }) {
  return (
    <section aria-label="Kategori alat" className="max-w-3xl space-y-4">
      <p className="text-sm text-zinc-400">Ubah nama ikut mengubah semua rujukan (alat, template, booking). Hapus hanya untuk kategori kosong.</p>
      <div className="flex flex-wrap gap-2">
        <Input data-testid="new-category-input" value={newCategory}
          onChange={(e) => setNewCategory(e.target.value)} placeholder="Nama kategori baru"
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); onAdd(); } }}
          className="h-12 max-w-xs rounded-xl border-white/10 bg-zinc-950" />
        <Button data-testid="add-category-button" disabled={busy || !newCategory.trim()} onClick={onAdd}
          className="h-12 rounded-full bg-[#0069DB] px-5 font-semibold text-white hover:bg-[#0058B6]">
          Tambah Kategori
        </Button>
      </div>
      <div data-testid="category-list" className="divide-y divide-white/5 overflow-hidden rounded-2xl border border-white/10 bg-zinc-900">
        {categories.map((c) => (
          <div key={c.name} data-testid={`category-row-${c.name.toLowerCase().replace(/[^a-z0-9]/g, "-")}`}
            className="flex min-h-[56px] flex-wrap items-center gap-3 px-4 py-3">
            {renaming?.old === c.name ? (
              <div className="flex flex-1 flex-wrap items-center gap-2">
                <Input data-testid="rename-category-input" value={renaming.value}
                  onChange={(e) => setRenaming({ ...renaming, value: e.target.value })}
                  className="h-10 max-w-xs rounded-lg border-white/10 bg-zinc-950" />
                <Button data-testid="save-category-rename" disabled={busy}
                  onClick={() => renaming.value.trim() && onRename(c.name, renaming.value.trim())}
                  className="h-10 rounded-full bg-[#0069DB] px-4 text-xs font-semibold text-white">Simpan</Button>
                <Button variant="outline" onClick={() => setRenaming(null)}
                  className="h-10 rounded-full border-white/15 bg-transparent px-4 text-xs text-zinc-300">Batal</Button>
              </div>
            ) : (
              <>
                <div className="min-w-0 flex-1">
                  <p className="break-words text-sm font-medium text-zinc-100">{c.name}</p>
                  <p className="font-mono text-xs tabular-nums text-zinc-500">{c.item_count} jenis &middot; {c.total_qty} pcs</p>
                </div>
                <Button data-testid={`rename-category-${c.name.toLowerCase().replace(/[^a-z0-9]/g, "-")}`} variant="outline"
                  onClick={() => setRenaming({ old: c.name, value: c.name })}
                  className="h-10 rounded-full border-white/10 bg-transparent px-4 text-xs text-zinc-300 hover:bg-white/10">
                  Ubah Nama
                </Button>
                <ConfirmDeleteCategory name={c.name} itemCount={c.item_count} busy={busy} onDelete={onDelete} />
              </>
            )}
          </div>
        ))}
        {categories.length === 0 && <p className="px-4 py-6 text-sm text-zinc-500">Belum ada kategori.</p>}
      </div>
    </section>
  );
}

function ConfirmDeleteCategory({ name, itemCount, busy, onDelete }) {
  const [open, setOpen] = useState(false);
  const slug = name.toLowerCase().replace(/[^a-z0-9]/g, "-");
  return (
    <>
      <Button data-testid={`delete-category-${slug}`} variant="outline" disabled={busy}
        onClick={() => setOpen(true)}
        className="h-10 rounded-full border-rose-500/40 bg-transparent px-4 text-xs text-rose-300 hover:bg-rose-500/10">
        Hapus
      </Button>
      <ConfirmDialog
        open={open}
        onOpenChange={setOpen}
        title={`Hapus kategori ${name}?`}
        description={itemCount > 0
          ? `Kategori ini dipakai ${itemCount} jenis alat. Backend menolak hapus kategori yang masih berisi - pindahkan dulu alatnya.`
          : "Kategori kosong dihapus permanen."}
        confirmLabel="Hapus kategori"
        testId="delete-category-confirm"
        pending={busy}
        onConfirm={() => { setOpen(false); onDelete(name); }}
      />
    </>
  );
}
